import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from "react-redux";
import { format } from "date-fns";
import toast from 'react-hot-toast';
import { 
    Box, 
    Typography, 
    CircularProgress, 
    FormControl, 
    MenuItem, 
    InputLabel, 
    Select,      
} from '@mui/material';
import useAuthCheck from 'hooks/useAuthCheck';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { filterCrewMember } from 'state/helpers';
import { countFlyingCrew, countAllCrewMembers, countItemsByTableau } from './utils';
import Header from 'components/Header';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import FilterButton from 'components/FilterButton';
import ChartDialog from 'components/chartNivo/ChartDialog';
import CrewFilterDialog from 'components/CrewFilterDialog';
import CommonTableau from './CommonTableau';
import SortBarChart from 'components/chartNivo/SortBarChart';
import SortBarMultiLinesChart from 'components/chartNivo/SortBarMultiLinesChart';
import PieChart from 'components/chartNivo/PieChart';
import { boxStyles as styles } from 'components/CustomStyles';


const displayOptions = [
    { value: "chart", label: "Chart" },
    { value: "tableau", label: "Tableau" },
];

const viewOptions = {
    chart: [
        { value: "crewByPostBase", label: "by Rank & Base" },
        { value: "crewByRank", label: "by Rank" },
        { value: "crewByBase", label: "by Base" },
    ],
    tableau: [
        { value: "byRankBase", label: "by Rank & Base" },
        { value: "byRank", label: "by Rank" },
        { value: "byBase", label: "by Base" },
    ],
};

const FlyingCrewCountChart = () => {    
    useAuthCheck();
    
    const { startDate, endDate } = useDateRangeContext();
    const crewRanksData = useSelector((state) => state.crewRanks);        
    
    const scrollRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [chartOpen, setChartOpen] = useState(false); 
    const [selectedChart, setSelectedChart] = useState(null);
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState({ isActive: 'ALL' });
    const defaultFilters = {
        isActive: 'ALL',
    };
    
    const [displayType, setDisplayType] = useState('chart');
    const [view, setView] = useState('crewByPostBase');

    useEffect(() => {
        const options = viewOptions[displayType];
        if (!options?.length) return;
        const isValid = options.some(opt => opt.value === view);
        if (!isValid) setView(options[0].value);
    }, [displayType, view]);

    const safeView = useMemo(() => {
        const options = viewOptions[displayType] || [];
        return options.some(o => o.value === view) ? view : options[0]?.value || '';
    }, [view, displayType]);
    const [filteredFlyingCrewList, setFilteredFlyingCrewList] = useState([]);
    const [data, setData] = useState({
        flyingCrewByPostBase: [],
        flyingCrewByPostBasePie: [],
        allCrewByPostBase: [],
        allCrewByPostBasePie: [],        
        flyingCrewByRank: [],           
        flyingCrewByRankPie: [],
        allCrewByRank: [],
        allCrewByRankPie: [],
        flyingCrewByBase: [],
        flyingCrewByBasePie: [],
        allCrewByBase: [],
        allCrewByBasePie: [],
    });
    
    const [activeCrewMembers, setActiveCrewMembers] = useState([]);
    const [flyingCrewMembers, setFlyingCrewMembers] = useState([]);
            
    const fetchCrewMembers = useCallback(async () => {
        const res = await api.get('/crewmembers/active');
        const crewList = res.data
            ?.map(m => {
                return {
                    crewCode: m.crewCode,
                    crewName: m.crewName,
                    post: m.rank,
                    base: m.location,
                    trainingQuals: m.trainingQuals,
                    quals: m.quals,
                };
            });

        setActiveCrewMembers(crewList);
    }, []);

    const fetchFlyingCrewMembers = useCallback(async () => {
        if (!startDate || !endDate) return; 

        const url = '/legmembers/flyingcrewmembers';
        const payload = { startDate, endDate };
        const res = await api.post(url, payload);
        
        setFlyingCrewMembers(res.data);
    }, [startDate, endDate]);
    
    useEffect(() => {
        const loadData = async () => {
            try {
                setError(null);
                setLoading(true);

                await Promise.all([
                    fetchCrewMembers(),
                    fetchFlyingCrewMembers(),
                ]);

            } catch (error) {
                setError(error);
                toast.error(error.response?.data?.message || "Failed to fetch data!");
            } finally {
                setLoading(false); 
            }
        };

        loadData();
    }, [    
        fetchCrewMembers,
        fetchFlyingCrewMembers,
    ]);

    const filterCrewMembers = useCallback((members) => {
        if (!Array.isArray(members)) {
            return []; 
        }
        
        if (!filters) return members; 

        return members.filter(member => filterCrewMember(member, filters));
    }, [filters]);  
    
    useEffect(() => {      
        try {            
            const activeCrewMap = new Map(
                activeCrewMembers?.map(c => [
                    c.crewCode,
                    { post: c.post, base: c.base }
                ])
            );

            const flyingCrewList = flyingCrewMembers?.map(m => {
                const activeInfo = activeCrewMap.get(m.crewCode);

                return {
                    crewCode: m.crewCode,
                    crewName: m.crewName,
                    post: activeInfo?.post ?? m.post,
                    base: activeInfo?.base ?? m.base,
                    trainingQuals: m.trainingQuals,
                    quals: m.quals,
                };
            }) ?? [];

            const filteredFlyingCrew = filterCrewMembers(flyingCrewList);
            setFilteredFlyingCrewList(filteredFlyingCrew);
            const filteredActiveCrew = filterCrewMembers(activeCrewMembers);

            const flyingSet = new Set(filteredFlyingCrew.map(m => m.crewCode));

            const merged = [...filteredActiveCrew, ...filteredFlyingCrew].map(member => ({
                ...member,
                isFlying: flyingSet.has(member.crewCode),
            }));
            const allCrewMerged = Array.from(
                new Map(merged.map(m => [m.crewCode, m])).values()
            );

            const results = {
                flyingCrewByPostBase: countFlyingCrew(filteredFlyingCrew, 'postbase'),
                flyingCrewByPostBasePie: countFlyingCrew(filteredFlyingCrew, 'postbase', 'pie'),
                allCrewByPostBase: countAllCrewMembers(allCrewMerged, 'postbase', 'stackedbar'),
                allCrewByPostBasePie: countAllCrewMembers(allCrewMerged, 'postbase', 'pie'),
                flyingCrewByRank: countFlyingCrew(filteredFlyingCrew, 'post'), 
                flyingCrewByRankPie: countFlyingCrew(filteredFlyingCrew, 'post', 'pie'),  
                allCrewByRank: countAllCrewMembers(allCrewMerged, 'post', 'stackedbar'),
                allCrewByRankPie: countAllCrewMembers(allCrewMerged, 'post', 'pie'),
                flyingCrewByBase: countFlyingCrew(filteredFlyingCrew, 'base'),
                flyingCrewByBasePie: countFlyingCrew(filteredFlyingCrew, 'base', 'pie'),
                allCrewByBase: countAllCrewMembers(allCrewMerged, 'base', 'stackedbar'),
                allCrewByBasePie: countAllCrewMembers(allCrewMerged, 'base', 'pie'),
            };
            
            setData(results);
        } catch(error) {
            setError(error);
        } 
    }, [activeCrewMembers, flyingCrewMembers, filterCrewMembers]);

    const tableauData = useMemo(() => {
        if (displayType !== 'tableau') return [];
        const fieldMap = {
            byRankBase: ['post', 'base'],
            byRank:     ['post', null],
            byBase:     ['base', null],
        };
        const fields = fieldMap[safeView];
        if (!fields) return [];
        return countItemsByTableau(filteredFlyingCrewList, fields[0], fields[1]);
    }, [displayType, safeView, filteredFlyingCrewList]);

    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);

    const handleFilterApply = (filterValues) => {
        setFilters(filterValues);
    };

    const {
        baseOptions,
        postOptions,
        trainingQualOptions,
    } = useMemo(() => {
        if (!Array.isArray(flyingCrewMembers) || flyingCrewMembers.length === 0) {
            return {
                baseOptions: [],
                postOptions: [],
                trainingQualOptions: [],
                isActiveOptions: [],
            };
        }

        const baseSet = new Set();
        const postSet = new Set();
        const trainingQualSet = new Set();
        const isActiveSet = new Set();

        flyingCrewMembers.forEach(m => {
            if (m.base) baseSet.add(m.base);
            if (m.post) postSet.add(m.post);
            if (m.isActive !== undefined) isActiveSet.add(m.isActive);

            m.trainingQuals?.forEach(q => q && trainingQualSet.add(q));
        });

        const rankMap = new Map(
            (crewRanksData || []).map(r => [r.rankCode, r])
        );

        const baseOptions = [...baseSet].sort();

        const postOptions = [...postSet]
            .map(post => rankMap.get(post))
            .filter(Boolean)
            .sort((a, b) => a.rankPriority - b.rankPriority)
            .map(r => r.rankCode);

        const trainingQualOptions = [...trainingQualSet].sort();

        const isActiveOptions = [...isActiveSet].sort();

        return {
            baseOptions,
            postOptions,
            trainingQualOptions,
            isActiveOptions,
        };
    }, [flyingCrewMembers, crewRanksData]);
    
    const handleChartOpen = (chart) => {
        setSelectedChart(
            <Box sx={{ ...styles }}>
                <Box 
                    height="100%" 
                    width={"100%"} 
                    sx={chart.style}
                >
                    {chart.component}
                </Box>                
            </Box>
        );
        setChartOpen(true);
    };

    const handleCloseDialog = () => {
        setSelectedChart(null);
        setChartOpen(false);
    };

    const period = `${startDate ? format(startDate, 'ddMMMyy') : ''} - ${endDate ? format(endDate, 'ddMMMyy') : ''}`;
    const defaultTitle = `Flying Crew ${period}`;
    const title = `All & Flying Crew ${period}`;
    const title2 = `Flying & Non-Flying Crew ${period}`;

    const chartMap = {
        crewByPostBase: [
            { component: <SortBarMultiLinesChart data={data.flyingCrewByPostBase} title={defaultTitle} bottomLegend='Base' leftLegend='Count' digit={0} bottomOffSet={10} bottomLegendOffSet={10} barTranslateXOffSet={80} lineTranslateXOffSet={-10} />, style: {} },
            { component: <PieChart data={data.flyingCrewByPostBasePie} title={defaultTitle} itemWidthOffSet={-30} />, style: {} },
            { component: <SortBarChart data={data.allCrewByPostBase} title={title} bottomLegend="Base" leftLegend="Count" isShowAvgLine={false} isSortDisabled={true} isHorizontalBottomLegend={false} bottomOffSet={10} bottomLegendOffSet={10} />, style: {} },
            { component: <PieChart data={data.allCrewByPostBasePie} title={title2} itemWidthOffSet={-20} />, style: {} },
        ],
        crewByRank: [
            { component: <SortBarMultiLinesChart data={data.flyingCrewByRank} title={defaultTitle} bottomLegend='Rank' leftLegend='Count' isHorizontalBottomLegend={true} digit={0} bottomOffSet={-30} bottomLegendOffSet={-30} barTranslateXOffSet={80} lineTranslateXOffSet={-10} />, style: {} },
            { component: <PieChart data={data.flyingCrewByRankPie} title={defaultTitle} itemWidthOffSet={-45} />, style: {} },
            { component: <SortBarChart data={data.allCrewByRank} title={title} bottomLegend="Rank" leftLegend="Count" isShowAvgLine={false} isSortDisabled={true} isHorizontalBottomLegend={true} bottomOffSet={-30} bottomLegendOffSet={-30} />, style: {} },
            { component: <PieChart data={data.allCrewByRankPie} title={title2} itemWidthOffSet={-20} />, style: {} },
        ],    
        crewByBase: [
            { component: <SortBarMultiLinesChart data={data.flyingCrewByBase} title={defaultTitle} bottomLegend='Base' leftLegend='Count' digit={0} bottomOffSet={10} bottomLegendOffSet={10} barTranslateXOffSet={80} lineTranslateXOffSet={-10} />, style: {} },
            { component: <PieChart data={data.flyingCrewByBasePie} title={defaultTitle} itemWidthOffSet={-30} />, style: {} },
            { component: <SortBarChart data={data.allCrewByBase} title={title} bottomLegend="Base" leftLegend="Count" isShowAvgLine={false} isSortDisabled={true} isHorizontalBottomLegend={false} bottomOffSet={10} bottomLegendOffSet={10} />, style: {} },
            { component: <PieChart data={data.allCrewByBasePie} title={title2} itemWidthOffSet={-20} />, style: {} },
        ],    
    }; 

    const renderCharts = (charts, customStyles) => (
        <Box
            ref={scrollRef}
            height={LAYOUT_HEIGHT}  
            display="flex"
            flexDirection="column"
            gap={2}
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
        >
            {charts.map((chart, index) => (
                <Box
                    key={index}
                    onDoubleClick={() => handleChartOpen(chart)}
                    sx={{ ...styles, ...customStyles }}
                >
                    <Box 
                        height="100%" 
                        width={"100%"} 
                        sx={chart.style}
                    >
                        {chart.component}
                    </Box>
                </Box>
            ))}
        </Box>
    );

    
    return (
        <Box className="main-container-box">                  
            <FlexBetween>
                <Header
                    title="Flying Crews"
                    subtitle={loading ? 'Loading...' : displayType === 'chart' ? 'List of Charts' : 'List of Tableaus'}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <FormControl 
                            size="small" 
                            sx={{ width: { xs: '6rem', sm: '8rem' } }}
                        >
                            <InputLabel>Display</InputLabel>
                            <Select
                                value={displayType}
                                label="Display"
                                onChange={(e) => setDisplayType(e.target.value)}
                            >
                                {displayOptions.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        
                        <FormControl
                            size="small"
                            sx={{ width: { xs: '6rem', sm: '10rem' } }}
                        >
                            <InputLabel id="crew-view-label">View</InputLabel>
                            <Select
                                labelId="crew-view-label"
                                label="View"
                                value={safeView}
                                onChange={(e) => setView(e.target.value)}
                            >
                                {viewOptions[displayType].map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FilterButton
                            filters={filters}
                            defaultFilters={defaultFilters}
                            onClick={handleFilterOpen}
                        />
                    </Box>
                )}                
            </FlexBetween>             
            {loading ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <Typography color="error">
                        Error: {error?.message || ''}
                    </Typography>
                </Box>
            ) : (
                <Box
                    display="grid"                        
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                >
                    {displayType === 'chart' && safeView in chartMap &&
                        renderCharts(chartMap[safeView], { minHeight: LAYOUT_HEIGHT })
                    }
                    {displayType === 'tableau' &&
                        renderCharts([{ component: <CommonTableau data={tableauData} mode="count" />, style: {} }], { minHeight: LAYOUT_HEIGHT })
                    }

                    <ScrollToTop scrollRef={scrollRef} />
                </Box>
            )}
      
            {chartOpen && (
                <ChartDialog
                    open={chartOpen}
                    onClose={handleCloseDialog}
                    chartComponent={selectedChart}
                />
            )}

            {isFilterDialogOpen &&
                <CrewFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    postOptions={postOptions}
                    baseOptions={baseOptions}
                    trainingQualOptions={trainingQualOptions}
                    currentFilters={filters}
                />
            }
        </Box>
    );
};

export default FlyingCrewCountChart;
